import PageNumber from 'components/PageNumber/PageNumber';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaChevronLeft, FaCogs } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';

export type DatasheetRow = {
  label?: ReactNode;
  value?: ReactNode;
  key?: string;
  className?: string;
  valueClassName?: string;
  valueTone?: 'default' | 'danger' | 'warning' | 'success' | 'muted';
  description?: ReactNode;
  meta?: ReactNode;
  tooltip?: ReactNode;
  render?: ReactNode;
};

export type DatasheetSection = {
  key?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  meta?: ReactNode;
  rows?: DatasheetRow[];
};

export type DatasheetProps<TRecord = Record<string, unknown>> = {
  title: ReactNode;
  subtitle?: ReactNode | ((record: TRecord | undefined, page: number) => ReactNode);
  records?: TRecord[];
  rows?: DatasheetRow[] | ((record: TRecord, page: number) => DatasheetRow[]);
  sections?: DatasheetSection[] | ((record: TRecord, page: number) => DatasheetSection[]);
  initialPage?: number;
  minBodyHeight?: number;
  loading?: boolean;
  loadingMessage?: ReactNode;
  emptyMessage?: ReactNode;
  errorMessage?: ReactNode;
  onClose?: () => void;
  onSettingsOpen?: React.MouseEventHandler<HTMLButtonElement>;
  settingsDisabled?: boolean;
  showSettings?: boolean;
  footer?: ReactNode;
  notice?: ReactNode;
  body?: ReactNode | ((record: TRecord, page: number) => ReactNode);
  media?: ReactNode | ((record: TRecord, page: number) => ReactNode);
  modal?: ReactNode;
  headerActions?: ReactNode;
  getRecordId?: (record: TRecord) => string | number | null | undefined;
  onPageChange?: (page: number, record: TRecord | undefined) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
};

/**
 * Shared Connected Devices datasheet renderer.
 *
 * @example
 * <Datasheet
 *   title="Device Data"
 *   records={device.data}
 *   getRecordId={(row) => row.did}
 *   sections={(row) => [{ title: 'Device Details', rows: [{ label: 'Battery', value: `${row.battery} V` }] }]}
 * />
 */
export default function Datasheet<TRecord = Record<string, unknown>>({
  title, // main header title
  subtitle, // (optional) header subtitle, static or derived from the selected record
  records = [], // paginated data records
  rows = [], // row definitions, static or derived from the selected record
  sections, // (optional) grouped row sections, static or derived from the selected record
  initialPage = 0, // default page when no URL match exists
  minBodyHeight = 540, // minimum scroll body height
  loading = false, // loading state for empty records
  loadingMessage = 'Loading device data...', // message shown while loading
  emptyMessage = 'No data available for this device.', // message shown when not loading and empty
  errorMessage, // (optional) the fetch failed: shown instead of the empty state, with a retry
  onClose, // back button handler
  onSettingsOpen, // settings button handler
  settingsDisabled = false, // disables settings button
  showSettings = true, // controls settings button visibility
  footer, // (optional) footer override, otherwise PageNumber renders
  notice, // (optional) content between header and body
  body, // (optional) custom body content, static or derived from selected record
  media, // (optional) media section, static or derived from selected record
  modal, // (optional) modal rendered with the datasheet
  headerActions, // (optional) header actions rendered before settings
  getRecordId, // maps a record to its URL/page id
  onPageChange, // callback when selected page changes
  onLoadMore, // (optional) fetches the next page of older records
  hasMore = false, // the server holds older records than `records`
  loadingMore = false, // a page fetch is in flight; disables Load more
}: DatasheetProps<TRecord>) {
  const [searchParams] = useSearchParams(); // current URL query params
  const [page, setPage] = useState(initialPage); // selected page index
  const hasRecords = Array.isArray(records) && records.length > 0; // records exist
  const safePage = hasRecords ? Math.min(Math.max(page, 0), records.length - 1) : 0; // clamped page
  const currentRecord = hasRecords ? records[safePage] : undefined; // selected record

  const pageByRecordId = useMemo(() => {
    const map = new Map<string, number>(); // record id to page index
    if (!getRecordId) return map; // no URL mapping requested
    records.forEach((record, index) => {
      const id = getRecordId(record); // device-specific record id
      if (id != null) map.set(String(id), index); // store valid ids only
    });
    return map;
  }, [records, getRecordId]);

  useEffect(() => {
    if (!hasRecords) {
      setPage(0); // reset empty data to page zero
      return;
    }

    const did = searchParams.get('did'); // shared datasheet URL record id
    if (did && pageByRecordId.has(String(did))) {
      setPage(pageByRecordId.get(String(did)) ?? 0); // jump to URL-selected record
      return;
    }

    // Load more appends OLDER records at the end, so the page a viewer is on is
    // still the same record: keep it. Fall back to the initial page only when
    // the current one no longer exists.
    setPage((prev) =>
      prev > 0 && prev < records.length
        ? prev
        : Math.min(Math.max(initialPage, 0), records.length - 1),
    );
  }, [hasRecords, initialPage, pageByRecordId, records.length, searchParams]);

  useEffect(() => {
    onPageChange?.(safePage, currentRecord); // notify consumers of active page
  }, [safePage, currentRecord, onPageChange]);

  const renderedRows = useMemo(() => {
    if (!currentRecord) return []; // no record means no rows
    return typeof rows === 'function' ? rows(currentRecord, safePage) : rows;
  }, [currentRecord, rows, safePage]);

  const renderedSections = useMemo(() => {
    if (!currentRecord) return []; // no record means no sections
    if (sections) {
      return typeof sections === 'function' ? sections(currentRecord, safePage) : sections; // resolve sections
    }
    return renderedRows.length ? [{ rows: renderedRows }] : []; // support row-only builders
  }, [currentRecord, renderedRows, safePage, sections]);

  const renderSlot = (slot: ReactNode | ((record: TRecord, page: number) => ReactNode)) => {
    if (!slot || !currentRecord) return null; // slots require a selected record
    return typeof slot === 'function' ? slot(currentRecord, safePage) : slot;
  };

  const renderedSubtitle =
    typeof subtitle === 'function' ? subtitle(currentRecord, safePage) : subtitle;

  const getValueToneClassName = (tone?: DatasheetRow['valueTone']) => {
    if (tone === 'danger') return 'text-danger fw-semibold';
    if (tone === 'warning') return 'text-warning fw-semibold';
    if (tone === 'success') return 'text-success fw-semibold';
    if (tone === 'muted') return 'text-theme-muted';
    return '';
  };

  const renderDatasheetRow = (row: DatasheetRow, index: number) =>
    row.render ? (
      // Fragment provides the required React key without adding layout markup.
      <React.Fragment key={row.key ?? String(index)}>{row.render}</React.Fragment>
    ) : (
      <div
        className={`d-flex justify-content-between gap-3 py-1 border-bottom border-theme-strong ${row.className ?? ''}`}
        key={row.key ?? String(index)}
        title={typeof row.tooltip === 'string' ? row.tooltip : undefined}
      >
        <div className="min-w-0 pe-2">
          <p className="mb-0 text-theme-muted">{row.label}:</p>
          {row.description ? (
            <small className="d-block text-theme-muted">{row.description}</small>
          ) : null}
        </div>
        <div
          className={`text-end text-break ${getValueToneClassName(row.valueTone)} ${row.valueClassName ?? ''}`}
        >
          {row.value}
          {row.meta ? <small className="d-block text-theme-muted">{row.meta}</small> : null}
        </div>
      </div>
    );

  const renderDatasheetSection = (section: DatasheetSection, sectionIndex: number) => (
    <section
      className="border border-theme-strong rounded-2 overflow-hidden mb-2"
      key={section.key ?? String(sectionIndex)}
    >
      {(section.title || section.subtitle || section.meta) && (
        <div className="px-2 py-1 border-bottom border-theme-strong bg-primary-shadow">
          <div className="d-flex justify-content-between gap-2 align-items-start">
            <div className="min-w-0">
              {section.title ? (
                <div className="fw-semibold text-wrap text-break">{section.title}</div>
              ) : null}
              {section.subtitle ? (
                <small className="d-block text-theme-muted text-wrap text-break">
                  {section.subtitle}
                </small>
              ) : null}
            </div>
            {section.meta ? (
              <small className="text-theme-muted text-end">{section.meta}</small>
            ) : null}
          </div>
        </div>
      )}
      <div className="px-2">{(section.rows ?? []).map(renderDatasheetRow)}</div>
    </section>
  );

  // A failed page is not a drained series: the sheet shows what the API said and
  // keeps the button, because the fetch leaves `hasMore` false after a failure
  // and the viewer would otherwise have no way to ask for the page again.
  const loadMoreButton =
    onLoadMore && (hasMore || errorMessage) ? (
      <Button
        size="sm"
        variant="primary"
        className="text-nowrap"
        onClick={onLoadMore}
        disabled={loadingMore}
        aria-label={errorMessage ? 'Retry loading records' : 'Load more records'}
      >
        {loadingMore ? 'Loading...' : errorMessage ? 'Retry' : 'Load more'}
      </Button>
    ) : null;

  const header = (
    <div className="d-flex justify-content-between align-items-center flex-shrink-0 px-2 py-2 m-2 mb-1 bg-primary-shadow border border-theme-strong rounded-2">
      <Button
        className="d-inline-flex align-items-center gap-2 bg-transparent border-0 text-reset px-2 py-1 rounded-1"
        onClick={onClose}
        aria-label="Back"
      >
        <FaChevronLeft size={18} />
        <span className="fw-semibold">Back</span>
      </Button>

      <div className="text-center flex-grow-1 min-w-0 px-3">
        <p className="h5 mb-0 fw-semibold d-block text-wrap text-break lh-sm">{title}</p>
        {renderedSubtitle ? (
          <div className="small text-white-75 text-wrap text-break mt-1">{renderedSubtitle}</div>
        ) : null}
      </div>

      <div
        className="d-flex align-items-center justify-content-end gap-1"
        style={{ minWidth: '5rem' }}
      >
        {headerActions}
        {showSettings ? (
          <Button
            className="d-inline-flex align-items-center justify-content-center bg-transparent border-0 text-reset p-1 rounded-1"
            onClick={onSettingsOpen}
            disabled={settingsDisabled || !onSettingsOpen}
            aria-label="Settings"
          >
            <FaCogs size={22} />
          </Button>
        ) : null}
      </div>
    </div>
  );

  if (!hasRecords) {
    return (
      <div className="connected-devices-datasheet flex-fill h-100 min-h-0 d-flex flex-column">
        {header}
        {/* Empty state: loading, failed and no-data share the same shell. */}
        <div className="flex-fill d-flex flex-column justify-content-center align-items-center text-center p-3">
          {loading ? (
            <>
              <div className="spinner-border mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-theme-muted mb-0">{loadingMessage}</p>
            </>
          ) : errorMessage ? (
            <>
              <p className="text-danger mb-0" role="alert">
                {errorMessage}
              </p>
              {loadMoreButton ? <div className="mt-3">{loadMoreButton}</div> : null}
            </>
          ) : (
            <p className="text-theme-muted mb-0">{emptyMessage}</p>
          )}
        </div>
        {modal}
      </div>
    );
  }

  return (
    <div className="connected-devices-datasheet flex-fill h-100 min-h-0 d-flex flex-column">
      {header}
      {notice}

      <div
        className="flex-fill min-h-0 overflow-auto px-2 py-1"
        style={{ minHeight: minBodyHeight }}
      >
        {renderedSections.map(renderDatasheetSection)}
        {renderSlot(body)}
        {renderSlot(media)}
      </div>

      <div className="flex-shrink-0">
        {/* The records already fetched stay readable; the failure is said above them. */}
        {errorMessage ? (
          <p className="text-danger small text-center mb-1" role="alert">
            {errorMessage}
          </p>
        ) : null}
        {footer ?? (
          <div className="d-flex justify-content-center align-items-center flex-wrap gap-2">
            <PageNumber pages={records.length} value={safePage} onChange={setPage} />
            {/* The server holds older records than the ones paged here, or the
                last page failed and this button asks for it again. */}
            {loadMoreButton}
          </div>
        )}
      </div>
      {modal}
    </div>
  );
}
